import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { resizeImage } from "@/lib/image";
import { startCheckout } from "@/pages/PaymentPages";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import ImageDrop from "@/components/ImageDrop";
import ScanHistory from "@/components/ScanHistory";
import { Sparkles, ArrowRight, ScanLine } from "lucide-react";

export default function ImageScanPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!file) return;
    setLoading(true);
    try {
      toast.loading(user?.plan === "surveillance" ? t("img.analyzing") : t("pay.redirect"), { id: "img" });
      const optimized = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", optimized);
      const { data } = await api.post("/scan/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.requires_payment) {
        await startCheckout("scan_full", null, { upload_ref: data.upload_ref, upload_kind: data.upload_kind });
      } else {
        toast.success(t("pay.done"), { id: "img" });
        nav(`/app/scan/${data.scan_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan.error"), { id: "img" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="image-scan-page">
      <div>
        <p className="eyebrow mb-1">{t("img.eyebrow")}</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">{t("img.title")}</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{t("img.sub")}</p>
      </div>

      <ImageDrop onFile={setFile} file={file} disabled={loading} testid="app-image-drop" hint={t("img.drop.hint")} />

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <Button onClick={run} disabled={loading || !file}
          className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 font-semibold w-full sm:w-auto"
          data-testid="image-scan-submit">
          <ScanLine className="w-4 h-4 mr-2" /> {loading ? "…" : t("img.cta")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <p className="font-mono text-xs text-muted-foreground">
          {user?.plan === "surveillance" ? t("img.included") : t("img.paid")}
        </p>
      </div>

      <div className="hairline p-5 rounded-lg flex items-start gap-3 bg-accent/40" data-testid="image-scan-note">
        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground leading-relaxed">{t("img.note")}</p>
      </div>

      <ScanHistory source="image" title="Historique — Analyses image" />
    </div>
  );
}
